/**
 * Fragment de routes du paquet v5-p02-missions-code-ecrans-2 (vague V5, règle R2 du découpage V4–V11 : un fragment par
 * paquet), rendu par le registre v10. Composants chargés à la demande (règle de chargement du registre). Le projet est
 * passé par `?projet=`, sinon celui de la coquille.
 * Aucune route du socle n'est remplacée, malgré les `couvre: route:…` des YAML (correctifs de revue, précédent v2-p06
 * pour `/org/acces`) :
 *  - `/tests`, `/feedback` et `/supervision` portent des gestes dont les écrans cibles v10 ne sont pas réalisés (créer un
 *    cas ou un plan, campagnes, envoyer un retour, lancer une mission) ;
 *  - `/pipelines` (revue 2) : des specs E2E historiques actives, hors de la zone du paquet (screens-t3v4 SoD-02,
 *    accessibility, accessibility-parcours-critiques cas 5), y vérifient PipelinesScreen ; la preuve SoD-02 « refus dit
 *    avant le clic » (B-009, FND-V050-190) y reste portée.
 * Les écrans du paquet ont leurs propres routes et mènent aux écrans historiques pour ces gestes :
 *  - `/tests/suivi` → projet-tests (`?vue=tnr`, `?vue=non-regression` inclus) ; `/feedback/generations` →
 *    projet-retours-generations ; `/supervision/agents` → projet-supervision-agents ; `/pipelines/suites` →
 *    projet-suites-de-commandes (création et détail par `/pipelines/suite`).
 * Le remplacement reviendra au paquet qui réalisera les écrans cibles et reprendra ces specs.
 * Routes neuves : les dix autres écrans du paquet (dialogues compris, chacun sa route).
 */
import { lazy, Suspense, type ComponentType, type JSX } from 'react'

import type { Session } from '../../auth/session'
import type { ContexteRoutes, FragmentRoutes } from './registre'

const DialogueDeciderMission = lazy(() => import('../ecrans/v5/missions-2/DialogueDeciderMission'))
const DialoguePiloterMission = lazy(() => import('../ecrans/v5/missions-2/DialoguePiloterMission'))
const ProjetTacheMissionFiche = lazy(() => import('../ecrans/v5/missions-2/ProjetTacheMissionFiche'))
const DialogueDeciderTacheMission = lazy(() => import('../ecrans/v5/missions-2/DialogueDeciderTacheMission'))
const ProjetTraitementEtape = lazy(() => import('../ecrans/v5/missions-2/ProjetTraitementEtape'))
const DialogueExecuterEtape = lazy(() => import('../ecrans/v5/missions-2/DialogueExecuterEtape'))
const DialoguePartagerApercu = lazy(() => import('../ecrans/v5/missions-2/DialoguePartagerApercu'))
const ProjetRevisionEssaiFiche = lazy(() => import('../ecrans/v5/missions-2/ProjetRevisionEssaiFiche'))
const DialogueVerifierFermerAnomalie = lazy(() => import('../ecrans/v5/missions-2/DialogueVerifierFermerAnomalie'))
const ProjetRetoursGenerations = lazy(() => import('../ecrans/v5/missions-2/ProjetRetoursGenerations'))
const ProjetSuitesDeCommandes = lazy(() => import('../ecrans/v5/missions-2/ProjetSuitesDeCommandes'))
const ProjetSuiteCommandesFiche = lazy(() => import('../ecrans/v5/missions-2/ProjetSuiteCommandesFiche'))
const ProjetSupervisionAgents = lazy(() => import('../ecrans/v5/missions-2/ProjetSupervisionAgents'))
const ProjetTests = lazy(() => import('../ecrans/v5/missions-2/ProjetTests'))

function ecran(Composant: ComponentType<{ session: Session }>) {
  return ({ session, t }: ContexteRoutes): JSX.Element => (
    <Suspense fallback={<p role="status">{t('v10.primitives.assemblage.chargement')}</p>}>
      <Composant session={session} />
    </Suspense>
  )
}

export const fragment: FragmentRoutes = {
  zone: 'v5-missions-2',
  routes: [
    { chemin: '/missions/:missionId/decider', element: ecran(DialogueDeciderMission) },
    { chemin: '/missions/:missionId/piloter', element: ecran(DialoguePiloterMission) },
    { chemin: '/missions/:missionId/taches/:taskId', element: ecran(ProjetTacheMissionFiche) },
    { chemin: '/missions/:missionId/taches/:taskId/decider', element: ecran(DialogueDeciderTacheMission) },
    { chemin: '/traitements/:jobId/etape', element: ecran(ProjetTraitementEtape) },
    { chemin: '/etapes/executer', element: ecran(DialogueExecuterEtape) },
    { chemin: '/apercus/partager/:previewId', element: ecran(DialoguePartagerApercu) },
    { chemin: '/apercus/revisions/:revision', element: ecran(ProjetRevisionEssaiFiche) },
    { chemin: '/anomalies/verifier', element: ecran(DialogueVerifierFermerAnomalie) },
    { chemin: '/feedback/generations', element: ecran(ProjetRetoursGenerations) },
    { chemin: '/pipelines/suites', element: ecran(ProjetSuitesDeCommandes) },
    { chemin: '/pipelines/suite', element: ecran(ProjetSuiteCommandesFiche) },
    { chemin: '/supervision/agents', element: ecran(ProjetSupervisionAgents) },
    { chemin: '/tests/suivi', element: ecran(ProjetTests) },
  ],
}
